const chapter = {
  id: 'atoms',
  slug: 'atoms',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Atoms',
        hindi: 'परमाणु',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Optics & Modern', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'rutherford',
      title: {
        english: 'Rutherford\'s Nuclear Model',
        hindi: 'रदरफोर्ड का नाभिकीय मॉडल',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Geiger–Marsden α-scattering experiment (gold foil). Most α-particles passed straight through, very few deflected by large angles, ~1 in 8000 bounced back. Conclusion: atom is mostly empty; positive charge and almost all mass concentrated in a tiny **nucleus** (size ~$10^{-15}$ m vs atom $\\sim 10^{-10}$ m).',
        hindi: 'गीगर-मार्सडेन α-स्कैटरिंग प्रयोग (सोने की पन्नी)। अधिकांश α-कण सीधे गुजर गए, बहुत कम बड़े कोणों से विक्षेपित हुए, 8000 में से 1 वापस उछल गए। निष्कर्ष: परमाणु अधिकतर खाली होता है; धनात्मक आवेश और लगभग सारा द्रव्यमान एक छोटे **नाभिक** (आकार ~$10^{-15}$ m बनाम परमाणु $\\sim 10^{-10}$ m) में केंद्रित होता है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Distance of closest approach** (head-on collision): $r_0 = \\dfrac{1}{4\\pi\\varepsilon_0}\\dfrac{2 Z e^2}{K}$ where $K$ is α KE.',
        hindi: '**निकटतम दृष्टिकोण की दूरी** (आमने-सामने की टक्कर): $r_0 = \\dfrac{1}{4\\pi\\varepsilon_0}\\dfrac{2 Z e^2}{K}$ जहां $K$ α KE है।',
            },
            {
              english: '**Impact parameter** $b = \\dfrac{Ze^2 \\cot(\\theta/2)}{4\\pi\\varepsilon_0 K}$ — small $b$ ⇒ large scattering angle $\\theta$.',
        hindi: '**प्रभाव पैरामीटर** $b = \\dfrac{Ze^2 \\cot(\\theta/2)}{4\\pi\\varepsilon_0 K}$ - छोटा $b$ ⇒ बड़ा प्रकीर्णन कोण $\\theta$।',
            },
            {
              english: 'Drawback: classical accelerated electrons would radiate and spiral into nucleus — atom would be unstable, and would emit a continuous spectrum (contradicted by observation).',
        hindi: 'दोष: शास्त्रीय त्वरित इलेक्ट्रॉन विकिरण करेंगे और नाभिक में सर्पिल होंगे - परमाणु अस्थिर होगा, और एक निरंतर स्पेक्ट्रम उत्सर्जित करेगा (अवलोकन द्वारा विरोधाभासी)।',
            },
          ],
        },
      ],
    },
    {
      id: 'bohr-model',
      title: {
        english: 'Bohr Model of Hydrogen-Like Atoms',
        hindi: 'बोर मॉडल — हाइड्रोजन सदृश परमाणु',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Postulate 1**: Electron revolves in fixed circular orbits without radiating.',
        hindi: '**अभिधारणा 1**: इलेक्ट्रॉन बिना विकिरण किए निश्चित गोलाकार कक्षाओं में घूमता है।',
            },
            {
              english: '**Postulate 2** (quantisation): Angular momentum $L = m v r = n\\hbar = nh/(2\\pi)$, $n = 1, 2, \\ldots$',
        hindi: '**अभिधारणा 2** (परिमाणीकरण): कोणीय गति $L = m v r = n\\hbar = nh/(2\\pi)$, $n = 1, 2, \\ldots$',
            },
            {
              english: '**Postulate 3** (frequency condition): Photon emitted/absorbed when electron jumps: $h\\nu = E_i - E_f$.',
        hindi: '**अभिधारणा 3** (आवृत्ति स्थिति): इलेक्ट्रॉन कूदने पर फोटॉन उत्सर्जित/अवशोषित होता है: $h\\nu = E_i - E_f$।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Bohr orbit results (hydrogen-like, atomic number $Z$)',
        hindi: 'बोह्र कक्षा परिणाम (हाइड्रोजन जैसा, परमाणु क्रमांक $Z$)',
          },
          statement: {
            english: `$$r_n = \\dfrac{n^2}{Z}\\,a_0,\\quad a_0 = 0.529\\ \\text{Å (Bohr radius)},$$
$$v_n = \\dfrac{Z}{n}\\,\\dfrac{c}{137},\\qquad E_n = -13.6\\,\\dfrac{Z^2}{n^2}\\ \\text{eV}.$$
Hence $K_n = -E_n = +13.6 Z^2/n^2$ eV, $U_n = 2E_n = -27.2 Z^2/n^2$ eV.`,
            hindi: '$$r_n = \\dfrac{n^2}{Z}\\,a_0,\\quad a_0 = 0.529\\\\\\\\ \\text{Å (Bohr radius)},$$ $$v_n = \\dfrac{Z}{n}\\,\\dfrac{c}{137},\\qquad E_n = -13.6\\,\\dfrac{Z^2}{n^2}\\\\\\\\ \\text{eV}.$$ इसलिए $K_n = -E_n = +13.6 Z^2/n^2$ eV, $U_n = 2E_n = -27.2 Z^2/n^2$ eV।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Ionisation energy of hydrogen: $13.6$ eV. Excitation energies: $10.2$ eV ($n{=}1\\to 2$), $12.09$ eV ($n{=}1\\to 3$).',
        hindi: 'हाइड्रोजन की आयनीकरण ऊर्जा: $13.6$ eV। उत्तेजना ऊर्जाएँ: $10.2$ eV ($n{=}1\\to 2$), $12.09$ eV ($n{=}1\\to 3$)।',
            },
            {
              english: 'Time period: $T_n \\propto n^3/Z^2$. Frequency $f_n \\propto Z^2/n^3$.',
        hindi: 'समयावधि: $T_n \\propto n^3/Z^2$. आवृत्ति $f_n \\propto Z^2/n^3$.',
            },
            {
              english: 'Number of spectral lines from level $n$: $\\dbinom{n}{2} = n(n-1)/2$.',
        hindi: 'स्तर $n$ से वर्णक्रमीय रेखाओं की संख्या: $\\dbinom{n}{2} = n(n-1)/2$।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Energy and radius of $n = 2$ orbit of $\\text{He}^+$ ($Z = 2$).',
        hindi: '$\\text{He}^+$ ($Z = 2$) की $n = 2$ कक्षा की ऊर्जा और त्रिज्या।',
          },
          solution: {
            english: '$E = -13.6\\cdot 4/4 = -13.6$ eV. $r = (4/2)a_0 = 2 a_0 = 1.058$ Å.',
        hindi: '$E = -13.6\\cdot 4/4 = -13.6$ eV. $r = (4/2)a_0 = 2 a_0 = 1.058$ Å.',
          },
        },
      ],
    },
    {
      id: 'spectrum',
      title: {
        english: 'Hydrogen Spectrum & Rydberg Formula',
        hindi: 'हाइड्रोजन स्पेक्ट्रम और रिडबर्ग सूत्र',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Rydberg formula',
        hindi: 'रिडबर्ग फॉर्मूला',
          },
          statement: {
            english: '$$\\dfrac{1}{\\lambda} = R_H Z^2\\left(\\dfrac{1}{n_f^2} - \\dfrac{1}{n_i^2}\\right),\\quad R_H = 1.097\\times 10^7\\ \\text{m}^{-1}.$$',
        hindi: '$$\\dfrac{1}{\\lambda} = R_H Z^2\\left(\\dfrac{1}{n_f^2} - \\dfrac{1}{n_i^2}\\right),\\quad R_H = 1.097\\times 10^7\\ \\text{m}^{-1}.$$',
          },
        },
        {
          type: 'table',
          headers: ['Series', '$n_f$', 'Region', 'Notes'],
          rows: [
            ['Lyman', '1', 'UV', 'All transitions to ground state'],
            ['Balmer', '2', 'Visible', 'H$_\\alpha$ (656 nm), H$_\\beta$, H$_\\gamma$, …'],
            ['Paschen', '3', 'IR', ''],
            ['Brackett', '4', 'IR', ''],
            ['Pfund', '5', 'Far IR', ''],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Series limit** of any series: $n_i \\to \\infty$ ⇒ shortest wavelength of that series.',
        hindi: '**किसी भी श्रृंखला की श्रृंखला सीमा**: $n_i \\to \\infty$ ⇒ उस श्रृंखला की सबसे छोटी तरंग दैर्ध्य।',
            },
            {
              english: 'Longest wavelength in Lyman: $n=2 \\to 1$, $\\lambda = 121.6$ nm.',
        hindi: 'लाइमैन में सबसे लंबी तरंग दैर्ध्य: $n=2 \\to 1$, $\\lambda = 121.6$ एनएम।',
            },
            {
              english: 'H$_\\alpha$ (Balmer, $3\\to 2$): $\\lambda = 656.3$ nm.',
        hindi: 'H$_\\alpha$ (बामर, $3\\to 2$): $\\lambda = 656.3$ एनएम।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Wavelength of first Balmer line of $\\text{He}^+$ ($Z = 2$).',
        hindi: '$\\text{He}^+$ ($Z = 2$) की पहली बामर लाइन की तरंग दैर्ध्य।',
          },
          solution: {
            english: '$1/\\lambda = R\\cdot 4(1/4 - 1/9) = R\\cdot 4\\cdot 5/36 = 5R/9$. $\\lambda = 9/(5R) = 164$ nm.',
        hindi: '$1/\\lambda = R\\cdot 4(1/4 - 1/9) = R\\cdot 4\\cdot 5/36 = 5R/9$. $\\lambda = 9/(5आर) = 164$ एनएम।',
          },
        },
      ],
    },
    {
      id: 'limitations',
      title: {
        english: 'Limitations of Bohr Model & de Broglie Justification',
        hindi: 'बोर मॉडल की सीमाएँ और द ब्रॉग्ली औचित्य',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Works only for hydrogen-like (one-electron) atoms.',
        hindi: 'केवल हाइड्रोजन जैसे (एक-इलेक्ट्रॉन) परमाणुओं के लिए काम करता है।',
            },
            {
              english: 'Cannot explain fine structure, Zeeman effect, Stark effect, intensity of spectral lines.',
        hindi: 'बारीक संरचना, ज़ीमैन प्रभाव, स्टार्क प्रभाव, वर्णक्रमीय रेखाओं की तीव्रता की व्याख्या नहीं कर सकते।',
            },
            {
              english: 'Mixes classical orbits with quantised angular momentum — incomplete, replaced by quantum mechanics.',
        hindi: 'शास्त्रीय कक्षाओं को परिमाणित कोणीय गति के साथ मिलाता है - अधूरा, क्वांटम यांत्रिकी द्वारा प्रतिस्थापित। **डी ब्रोगली का परिमाणीकरण का औचित्य**: वृत्ताकार कक्षा पर स्थिर पदार्थ तरंग के लिए की आवश्यकता होती है, जो देता है - बिल्कुल बोह्र का दूसरा अभिधारणा।',
            },
          ],
        },
        {
          type: 'paragraph',
          text: {
            english: '**de Broglie\'s justification of quantisation**: standing matter wave on circular orbit requires $2\\pi r_n = n\\lambda = n h/(m v)$, giving $m v r_n = nh/(2\\pi)$ — exactly Bohr\'s second postulate.',
        hindi: '**डी ब्रोगली का परिमाणीकरण का औचित्य**: वृत्ताकार कक्षा पर स्थिर पदार्थ तरंग के लिए $2\\pi r_n = n\\lambda = n h/(m v)$ की आवश्यकता होती है, जो $m v r_n = nh/(2\\pi)$ देता है - बिल्कुल बोह्र का दूसरा अभिधारणा।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'r_n = n^2 a_0/Z',
      desc: {
        english: 'Bohr radius',
        hindi: 'बोहर त्रिज्या',
      },
    },
    {
      latex: 'E_n = -13.6\\,Z^2/n^2\\ \\text{eV}',
      desc: {
        english: 'Energy levels',
        hindi: 'ऊर्जा स्तर',
      },
    },
    {
      latex: 'L = nh/(2\\pi)',
      desc: {
        english: 'Quantisation',
        hindi: 'परिमाणीकरण',
      },
    },
    {
      latex: '1/\\lambda = R Z^2 (1/n_f^2 - 1/n_i^2)',
      desc: {
        english: 'Rydberg',
        hindi: 'रिडबर्ग',
      },
    },
    {
      latex: 'r_0 = 2kZe^2/K',
      desc: {
        english: 'Closest approach',
        hindi: 'निकटतम दृष्टिकोण',
      },
    },
    {
      latex: 'N_{\\text{lines}} = n(n-1)/2',
      desc: {
        english: 'Lines from level $n$',
        hindi: 'स्तर $n$ से लाइनें',
      },
    },
    {
      latex: '2\\pi r_n = n\\lambda',
      desc: {
        english: 'de Broglie standing wave',
        hindi: 'डी ब्रोगली खड़ी लहर',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Bohr',
      problem: {
        english: 'Energy of $n = 3$ level of H atom?',
        hindi: 'H परमाणु के $n = 3$ स्तर की ऊर्जा?',
      },
      answer: {
        english: '$-1.51$ eV',
        hindi: '$-1.51$ eV',
      },
      solution: {
        english: '$E_3 = -13.6/9$.',
        hindi: '$E_3 = -13.6/9$।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Series',
      problem: {
        english: 'Number of spectral lines from $n = 4$?',
        hindi: '$n = 4$ से वर्णक्रमीय रेखाओं की संख्या?',
      },
      answer: {
        english: '$6$',
        hindi: '$6$',
      },
      solution: {
        english: '$\\binom{4}{2} = 6$.',
        hindi: '$\\binom{4}{2} = 6$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Hydrogen-like',
      problem: {
        english: 'Ionisation energy of $\\text{Li}^{2+}$ ($Z = 3$)?',
        hindi: '$\\text{Li}^{2+}$ ($Z = 3$) की आयनीकरण ऊर्जा?',
      },
      answer: {
        english: '$122.4$ eV',
        hindi: '$122.4$ eV',
      },
      solution: {
        english: '$13.6 \\times 9 = 122.4$.',
        hindi: '$13.6 \\times 9 = 122.4$.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Closest approach',
      problem: {
        english: 'α-particle ($K = 5$ MeV) on gold ($Z = 79$). Closest approach?',
        hindi: 'सोने पर α-कण ($K = 5$ MeV) ($Z = 79$)। निकटतम दृष्टिकोण?',
      },
      answer: {
        english: '$\\approx 4.5\\times 10^{-14}$ m',
        hindi: '$\\approx 4.5\\times 10^{-14}$ मी',
      },
      solution: {
        english: '$r_0 = 2kZe^2/K$ with $K = 5\\times 10^6 \\cdot 1.6\\times 10^{-19}$ J.',
        hindi: '$r_0 = 2kZe^2/K$ $K = 5\\times 10^6 \\cdot 1.6\\times 10^{-19}$ J के साथ।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Balmer',
      problem: {
        english: 'Wavelength of first member of Lyman series in H atom?',
        hindi: 'H परमाणु में लाइमैन श्रृंखला के पहले सदस्य की तरंगदैर्घ्य?',
      },
      answer: {
        english: '$121.6$ nm',
        hindi: '$121.6$ एनएम',
      },
      solution: {
        english: '$1/\\lambda = R(1 - 1/4) = 3R/4$.',
        hindi: '$1/\\lambda = R(1 - 1/4) = 3R/4$.',
      },
    },
  ],
  next: {
    slug: 'nuclei',
    title: 'Nuclei',
  },
};

export default chapter;
